import { inject } from '@angular/core';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStoreFeature, type, withMethods } from '@ngrx/signals';
import {
  EntityProps,
  EntityState,
  removeEntity,
  setAllEntities,
  updateEntity,
  upsertEntity,
} from '@ngrx/signals/entities';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { pipe, switchMap, tap } from 'rxjs';

import { SelectedEntityState } from '../../../shared/store/selected-entity.feature';
import { Site } from '../model/site.model';
import { SiteCreateRequest } from '../request/create-request';
import { SiteService } from '../site.service';
import { v7 as uuidv7 } from 'uuid';
import { ToastrErrorComponent } from '@shared/components/toastr/toastr-error.component';
import { toast } from 'ngx-sonner';

export function avecMéthodesSites<_>() {
  return signalStoreFeature(
    {
      state: type<EntityState<Site> & SelectedEntityState>(),
      props: type<EntityProps<Site>>(),
    },
    withMethods((store) => ({
      chargerSites: (state: { sites: Site[] }) => patchState(store, setAllEntities(state.sites)),
      ajouterSite: (state: { site: Site }) => patchState(store, upsertEntity(state.site)),
      modifierSite: (state: { site: Site }) =>
        patchState(store, updateEntity({ id: state.site.id, changes: state.site })),
      supprimerSites: (id: string) => patchState(store, removeEntity(id)),
    })),
    withMethods((store, service = inject(SiteService)) => ({
      modifier: rxMethod<{ site: Site; next: () => void }>(
        pipe(
          tap(({ site }) => store.modifierSite({ site })),
          switchMap(({ site, next }) =>
            service
              .modifier(site.id, { nom: site.nom, region: site.region, idsEtapes: site.etapes.map((x) => x.id) })
              .pipe(
                tapResponse({
                  next: () => next(),
                  error: (err) => {
                    const toastrId = uuidv7();
                    toast.custom(ToastrErrorComponent, {
                      componentProps: {
                        message: `Un problème est survenu lors de la modifications de ce site, contactez le support.`,
                        id: toastrId,
                      },
                      id: toastrId,
                    });
                    console.error(err);
                  },
                }),
              ),
          ),
        ),
      ),
      ajouter: rxMethod<{
        data: SiteCreateRequest;
        next?: () => void;
      }>(
        pipe(
          switchMap(({ data, next }) =>
            service.créer(data).pipe(
              tapResponse({
                next: () => next?.(),
                error: (err) => {
                  console.error("Error lors de l'ajout d'un site via le Store: ", err);
                  const toastrId = uuidv7();
                  toast.custom(ToastrErrorComponent, {
                    componentProps: {
                      message: `Un problème est survenu lors de la création du site, contactez le support.`,
                      id: toastrId,
                    },
                    id: toastrId,
                  });
                },
              }),
            ),
          ),
        ),
      ),
      charger: rxMethod<string>(
        pipe(
          switchMap((idCartographie) =>
            service.trouverTous(idCartographie).pipe(
              tapResponse({
                next: (response) => {
                  store.chargerSites({ sites: response.items });
                },
                error: (err) => {
                  console.error('Error lors du chargement des sites via le Store: ', err);
                  const toastrId = uuidv7();
                  toast.custom(ToastrErrorComponent, {
                    componentProps: {
                      message: `Un problème est survenu lors du chargement des sites, contactez le support.`,
                      id: toastrId,
                    },
                    id: toastrId,
                  });
                },
              }),
            ),
          ),
        ),
      ),
      trouverParId: rxMethod<{
        id: string;
        next?: (site: Site) => void;
      }>(
        pipe(
          switchMap(({ id, next }) =>
            service.trouverParId(id).pipe(
              tapResponse({
                next: (site) => {
                  store.ajouterSite({ site });
                  next?.(site);
                },
                error: (err) => {
                  console.error('Error dans le Store: ', err);
                  const toastrId = uuidv7();
                  toast.custom(ToastrErrorComponent, {
                    componentProps: {
                      message: `Un problème est survenu lors de la recherche de ce site, contactez le support.`,
                      id: toastrId,
                    },
                    id: toastrId,
                  });
                },
              }),
            ),
          ),
        ),
      ),
      supprimer: rxMethod<string>(
        pipe(
          switchMap((id) =>
            service.supprimer(id).pipe(
              tapResponse({
                next: () => store.supprimerSites(id),
                error: (err) => {
                  console.error(err);
                  const toastrId = uuidv7();
                  toast.custom(ToastrErrorComponent, {
                    componentProps: {
                      message: `Un problème est survenu lors de la suppression du site, contactez le support.`,
                      id: toastrId,
                    },
                    id: toastrId,
                  });
                },
              }),
            ),
          ),
        ),
      ),
    })),
  );
}
